import { useCallback, useEffect, useState } from 'react';

import {
    getLocalStorageItem,
    setLocalStorageItem,
} from '@/shared/lib/local-storage';
import { logError } from '@/shared/lib/logging';
import {
    DEFAULT_TRADER_TYPE,
    type TraderType,
} from '@/shared/types/trader-type';

const TRADER_TYPE_STORAGE_KEY = 'trader-type';

export function useTraderType() {
    const [traderType, setTraderType] =
        useState<TraderType>(DEFAULT_TRADER_TYPE);

    const updateTraderType = useCallback((newTraderType: TraderType) => {
        setTraderType(newTraderType);
        setLocalStorageItem(TRADER_TYPE_STORAGE_KEY, newTraderType);

        // Dispatch custom event for same-tab updates
        window.dispatchEvent(
            new CustomEvent('traderTypeChanged', {
                detail: { traderType: newTraderType },
            }),
        );
    }, []);

    // Load initial value from localStorage
    useEffect(() => {
        const storedTraderType = getLocalStorageItem<TraderType>(
            TRADER_TYPE_STORAGE_KEY,
            DEFAULT_TRADER_TYPE,
        );

        setTraderType(storedTraderType);
    }, []);

    // Listen for localStorage changes and custom events
    useEffect(() => {
        const handleStorageChange = (event: StorageEvent) => {
            if (event.key === TRADER_TYPE_STORAGE_KEY && event.newValue) {
                try {
                    const newTraderType = JSON.parse(
                        event.newValue,
                    ) as TraderType;

                    setTraderType(newTraderType);
                } catch (error) {
                    logError(
                        'Failed to parse trader type from localStorage',
                        error,
                    );
                }
            }
        };

        const handleCustomEvent = (event: CustomEvent) => {
            if (event.detail?.traderType) {
                setTraderType(event.detail.traderType);
            }
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener(
            'traderTypeChanged',
            handleCustomEvent as EventListener,
        );

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener(
                'traderTypeChanged',
                handleCustomEvent as EventListener,
            );
        };
    }, []);

    return {
        traderType,
        updateTraderType,
    };
}
